import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Loader2, Gift } from "lucide-react";

export default function AdminReferrals() {
  const [d, setD] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/referrals-stats"); setD(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to load."); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (value) => {
    setSaving(true);
    try {
      await api.put("/admin/referral-config", { enabled: value });
      setD((p) => ({ ...p, enabled: value }));
      toast.success(`Referral engine ${value ? "enabled" : "disabled"}.`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to update."); }
    finally { setSaving(false); }
  };

  if (!d) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  const cards = [
    { label: "Total referrals", value: d.total },
    { label: "Qualified", value: d.qualified },
    { label: "Pending", value: d.pending },
  ];

  return (
    <div className="space-y-6" data-testid="admin-referrals">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-2"><Gift className="h-7 w-7 text-[#24468C]" strokeWidth={1.75} /> Referrals</h1>
        <p className="text-muted-foreground text-sm">Users earn +{d.bonus_days} trial days for every {d.threshold} verified referrals.</p>
      </div>

      <Card className="border-zinc-200">
        <CardContent className="p-5 flex items-center justify-between">
          <div>
            <p className="font-semibold">Referral engine</p>
            <p className="text-sm text-muted-foreground">{d.enabled ? "Active — referrals and bonus days are being tracked." : "Disabled — no new referrals are recorded."}</p>
          </div>
          <Switch checked={d.enabled} disabled={saving} onCheckedChange={toggle} data-testid="referral-toggle" />
        </CardContent>
      </Card>

      <div className="grid grid-cols-3 gap-4">
        {cards.map((c) => (
          <Card key={c.label} className="border-zinc-200"><CardContent className="p-5">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{c.label}</p>
            <p className="font-heading text-3xl font-bold mt-1" data-testid={`referral-stat-${c.label.toLowerCase().replace(/ /g, "-")}`}>{c.value}</p>
          </CardContent></Card>
        ))}
      </div>

      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Top referrers</p>
        <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
          {d.leaders.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground" data-testid="referrals-leaderboard-empty">No qualified referrals yet.</div>
          ) : (
            <table className="w-full text-sm" data-testid="referrals-leaderboard">
              <thead>
                <tr className="border-b border-zinc-200 text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3 font-semibold">Email</th>
                  <th className="px-4 py-3 font-semibold">Code</th>
                  <th className="px-4 py-3 font-semibold text-right">Qualified</th>
                  <th className="px-4 py-3 font-semibold text-right">Bonus days</th>
                </tr>
              </thead>
              <tbody>
                {d.leaders.map((l) => (
                  <tr key={l.user_id} className="border-b border-zinc-100" data-testid={`leader-row-${l.user_id}`}>
                    <td className="px-4 py-3 font-medium">{l.email || "—"}</td>
                    <td className="px-4 py-3 font-mono text-xs text-[#24468C]">{l.code || "—"}</td>
                    <td className="px-4 py-3 text-right">{l.qualified}</td>
                    <td className="px-4 py-3 text-right">{l.bonus_days_granted}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
